import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Cart } from '@harbor/shared';
import { ApiError, apiRequest } from '../lib/api.js';
import { useAuth } from './auth.js';

/**
 * Cart state.
 *
 * ## The server owns the cart
 *
 * It would be simpler to keep the basket in `localStorage` and reconcile at
 * checkout. That is wrong for a shop: stock is finite and shared, so a basket
 * that only exists in the browser cannot tell you an item sold out while you
 * were deciding. Every mutation here goes to the server and the authoritative
 * cart comes back, which is also why `notice` can say "only 3 left" honestly.
 *
 * ## Why the total is not computed here
 *
 * The response already carries the totals, computed by the same
 * `computeTotals` the checkout endpoint uses. Recomputing them on the client
 * would be a second implementation of the tax and shipping rules, and the first
 * time those diverge the customer is charged a number they never saw.
 */

interface CartContextValue {
  cart: Cart | null;
  loading: boolean;
  /** True while a mutation is in flight, so buttons can disable themselves. */
  mutating: boolean;
  error: string | null;
  notice: string | null;
  itemCount: number;
  addItem: (productId: string, quantity?: number) => Promise<void>;
  updateItem: (itemId: string, quantity: number) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  clear: () => Promise<void>;
  refresh: () => Promise<void>;
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  dismissNotice: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

interface CartResponse extends Cart {
  notice?: string | null;
}

export function CartProvider({ children }: { children: ReactNode }) {
  const { user, status } = useAuth();

  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const apply = useCallback((response: CartResponse) => {
    setCart(response);
    setNotice(response.notice ?? null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      apply(await apiRequest<CartResponse>('/api/cart'));
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load your basket');
    } finally {
      setLoading(false);
    }
  }, [apply]);

  /**
   * Reload whenever the identity changes.
   *
   * Signing in merges the guest basket into the member's basket server-side, so
   * the cart the browser is holding is stale the moment the user authenticates.
   * Waiting for `status` to leave `loading` avoids firing this request before the
   * session has been restored, which would fetch the guest cart and then
   * immediately overwrite it.
   */
  useEffect(() => {
    if (status === 'loading') return;
    void refresh();
  }, [status, user?.id, refresh]);

  const run = useCallback(
    async (operation: () => Promise<CartResponse>) => {
      setMutating(true);
      setError(null);
      try {
        apply(await operation());
      } catch (caught) {
        const message =
          caught instanceof ApiError
            ? caught.problem.errors?.[0]?.message ?? caught.message
            : 'Something went wrong';
        setError(message);
        // A 409 means the server's view of the basket changed under us — a
        // product sold out, or the price moved. Re-reading is the only way to
        // get back in sync, and doing it here means every caller gets that for
        // free rather than remembering to handle it.
        if (caught instanceof ApiError && (caught.status === 409 || caught.status === 422)) {
          void refresh();
        }
      } finally {
        setMutating(false);
      }
    },
    [apply, refresh],
  );

  const addItem = useCallback(
    async (productId: string, quantity = 1) => {
      await run(() =>
        apiRequest<CartResponse>('/api/cart/items', {
          method: 'POST',
          body: { productId, quantity },
        }),
      );
      setDrawerOpen(true);
    },
    [run],
  );

  const updateItem = useCallback(
    async (itemId: string, quantity: number) => {
      await run(() =>
        apiRequest<CartResponse>(`/api/cart/items/${itemId}`, { method: 'PATCH', body: { quantity } }),
      );
    },
    [run],
  );

  const removeItem = useCallback(
    async (itemId: string) => {
      await run(() => apiRequest<CartResponse>(`/api/cart/items/${itemId}`, { method: 'DELETE' }));
    },
    [run],
  );

  const clear = useCallback(async () => {
    setMutating(true);
    try {
      await apiRequest('/api/cart', { method: 'DELETE' });
      await refresh();
    } finally {
      setMutating(false);
    }
  }, [refresh]);

  const value = useMemo<CartContextValue>(
    () => ({
      cart,
      loading,
      mutating,
      error,
      notice,
      itemCount: cart?.itemCount ?? 0,
      addItem,
      updateItem,
      removeItem,
      clear,
      refresh,
      drawerOpen,
      openDrawer: () => setDrawerOpen(true),
      closeDrawer: () => setDrawerOpen(false),
      dismissNotice: () => setNotice(null),
    }),
    [cart, loading, mutating, error, notice, addItem, updateItem, removeItem, clear, refresh, drawerOpen],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used inside <CartProvider>');
  return context;
}
